import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import { JWT_KEY } from '../lib/api'

/**
 * AuthProvider — single source of JWT truth for the PWA.
 *
 * Persistence layer is `localStorage['clearroute:jwt']`; the provider
 * mirrors it into React state so components re-render when the JWT
 * changes (AuthCallback → /, or apiGet on invalid-token path).
 *
 * Cross-tab sync: the `storage` event fires in OTHER tabs when a
 * given origin mutates localStorage. Good UX nice-to-have — if the
 * user signs in on tab A, tab B's App body flips to MapView without
 * a refresh. Same-tab updates come from `setJwt` directly.
 */

type AuthContextValue = {
  jwt: string | null
  setJwt: (value: string | null) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function readInitialJwt(): string | null {
  try {
    return localStorage.getItem(JWT_KEY)
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [jwt, setJwtState] = useState<string | null>(() => readInitialJwt())

  const setJwt = useCallback((value: string | null) => {
    try {
      if (value) localStorage.setItem(JWT_KEY, value)
      else localStorage.removeItem(JWT_KEY)
    } catch {
      // localStorage may be blocked (Safari private mode / iframe sandbox).
      // State still flips so the current session proceeds; next reload
      // drops the user back to SignIn, which is the right UX.
    }
    setJwtState(value)
  }, [])

  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key !== JWT_KEY) return
      setJwtState(event.newValue)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  return (
    <AuthContext.Provider value={{ jwt, setJwt }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used inside an <AuthProvider>')
  }
  return ctx
}
